import { useEffect, useRef, useState } from 'react';

const getCanvasHeight = (theme) => {
  if (typeof window === 'undefined') {
    return theme === 'water' ? 360 : 256;
  }

  if (theme === 'water') {
    return Math.max(320, Math.min(Math.round(window.innerHeight * 0.48), 440));
  }

  return 256;
};

const clampLevel = (fireLevel) => Math.min(Math.max(fireLevel, 1), 20);

const createBubble = (width, height, waterTop) => ({
  x: Math.random() * width,
  y: waterTop + Math.random() * (height - waterTop),
  radius: 2 + Math.random() * 6,
  speed: 0.6 + Math.random() * 1.4,
  drift: (Math.random() - 0.5) * 0.7,
  alpha: 0.18 + Math.random() * 0.32
});

export default function FireCanvas({ fireLevel = 1, theme = 'fire' }) {
  const canvasRef = useRef(null);
  const [dimensions, setDimensions] = useState({
    width: 10,
    height: getCanvasHeight(theme)
  });

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: Math.max(window.innerWidth, 10),
        height: getCanvasHeight(theme)
      });
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [theme]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return undefined;
    }

    const ctx = canvas.getContext('2d', { willReadFrequently: theme === 'fire' });
    if (!ctx) {
      return undefined;
    }

    const canvasWidth = dimensions.width;
    const canvasHeight = dimensions.height;

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    if (canvasWidth <= 0 || canvasHeight <= 0) {
      return undefined;
    }

    let animationFrameId;

    if (theme === 'water') {
      const normalizedLevel = clampLevel(fireLevel);
      const fillRatio = 0.24 + (normalizedLevel / 20) * 0.58;
      const waterTopBase = canvasHeight - canvasHeight * fillRatio;
      const bubbles = Array.from({ length: 18 + normalizedLevel }, () =>
        createBubble(canvasWidth, canvasHeight, waterTopBase)
      );

      const drawWaveLayer = (baseY, amplitude, wavelength, phase, fillStyle) => {
        ctx.beginPath();
        ctx.moveTo(0, canvasHeight);
        ctx.lineTo(0, baseY);

        for (let x = 0; x <= canvasWidth; x += 8) {
          const waveY =
            baseY +
            Math.sin(x / wavelength + phase) * amplitude +
            Math.sin(x / (wavelength * 0.55) + phase * 1.6) * amplitude * 0.28;
          ctx.lineTo(x, waveY);
        }

        ctx.lineTo(canvasWidth, canvasHeight);
        ctx.closePath();
        ctx.fillStyle = fillStyle;
        ctx.fill();
      };

      const renderWater = () => {
        const time = performance.now() * 0.001;
        const waterTop = waterTopBase + Math.sin(time * 1.2) * 6;

        ctx.clearRect(0, 0, canvasWidth, canvasHeight);

        const glowGradient = ctx.createLinearGradient(0, waterTop, 0, canvasHeight);
        glowGradient.addColorStop(0, 'rgba(146, 228, 255, 0.15)');
        glowGradient.addColorStop(0.45, 'rgba(54, 162, 219, 0.3)');
        glowGradient.addColorStop(1, 'rgba(7, 58, 108, 0.72)');
        drawWaveLayer(
          waterTop + 24,
          12,
          42,
          time * 1.15,
          glowGradient
        );

        const mainGradient = ctx.createLinearGradient(0, waterTop, 0, canvasHeight);
        mainGradient.addColorStop(0, 'rgba(184, 240, 255, 0.86)');
        mainGradient.addColorStop(0.12, 'rgba(110, 203, 241, 0.92)');
        mainGradient.addColorStop(0.62, 'rgba(32, 130, 198, 0.97)');
        mainGradient.addColorStop(1, 'rgba(10, 67, 125, 0.98)');
        drawWaveLayer(
          waterTop,
          16 + normalizedLevel * 0.24,
          55,
          time * 1.45,
          mainGradient
        );

        const shadowGradient = ctx.createLinearGradient(0, waterTop + 18, 0, canvasHeight);
        shadowGradient.addColorStop(0, 'rgba(22, 121, 194, 0.22)');
        shadowGradient.addColorStop(1, 'rgba(5, 35, 81, 0.5)');
        drawWaveLayer(
          waterTop + 14,
          10 + normalizedLevel * 0.18,
          34,
          -time * 1.85,
          shadowGradient
        );

        ctx.beginPath();
        for (let x = 0; x <= canvasWidth; x += 14) {
          const y = waterTop + Math.sin(x / 48 + time * 2.1) * 6;
          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
        ctx.lineWidth = 2;
        ctx.stroke();

        bubbles.forEach((bubble) => {
          bubble.y -= bubble.speed;
          bubble.x += Math.sin(time + bubble.y / 40) * 0.12 + bubble.drift;

          if (bubble.y + bubble.radius < waterTop) {
            Object.assign(bubble, createBubble(canvasWidth, canvasHeight, waterTop));
            bubble.y = canvasHeight + bubble.radius;
          }

          if (bubble.x < -bubble.radius) {
            bubble.x = canvasWidth + bubble.radius;
          } else if (bubble.x > canvasWidth + bubble.radius) {
            bubble.x = -bubble.radius;
          }

          ctx.beginPath();
          ctx.arc(bubble.x, bubble.y, bubble.radius, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 255, 255, ${bubble.alpha})`;
          ctx.fill();
          ctx.beginPath();
          ctx.arc(
            bubble.x - bubble.radius * 0.25,
            bubble.y - bubble.radius * 0.25,
            Math.max(1, bubble.radius * 0.28),
            0,
            Math.PI * 2
          );
          ctx.fillStyle = `rgba(255, 255, 255, ${bubble.alpha + 0.1})`;
          ctx.fill();
        });

        animationFrameId = requestAnimationFrame(renderWater);
      };

      renderWater();
    } else {
      const fps = 8;
      const speedFactor = 0.6;
      const normalizedLevel = clampLevel(fireLevel);
      const threshold = 1 - (0.1 + normalizedLevel * 0.04);
      const fireHeightFactor = 0.8 + normalizedLevel / 40;
      const imageData = ctx.createImageData(canvasWidth, canvasHeight);
      const data = imageData.data;
      const fire = new Array(canvasWidth * canvasHeight).fill(0);
      const colors = Array.from({ length: 256 }, () => [0, 0, 0]);

      for (let i = 0; i < 25; ++i) {
        colors[i][0] = 0;
        colors[i][1] = Math.floor(137 * (i / 25));
        colors[i][2] = Math.floor(255 * (i / 25));
      }

      for (let i = 0; i < 20; ++i) {
        const idx = i + 25;
        colors[idx][0] = Math.floor(255 * (i / 20));
        colors[idx][1] = 137 - Math.floor(i * 3);
        colors[idx][2] = 255 - Math.floor(i * 8);
      }

      for (let i = 0; i < 40; ++i) {
        const idx = i + 45;
        colors[idx][0] = 255;
        colors[idx][1] = 80 + Math.floor(i * 3);
        colors[idx][2] = Math.floor(80 * (1 - i / 40));
      }

      for (let i = 0; i < 50; ++i) {
        const idx = i + 85;
        colors[idx][0] = 255;
        colors[idx][1] = 200 + Math.floor(i * 1.1);
        colors[idx][2] = Math.floor(i * 4);
      }

      for (let i = 0; i < 60; ++i) {
        const idx = i + 135;
        colors[idx][0] = 255;
        colors[idx][1] = 255;
        colors[idx][2] = 80 + Math.floor(i * 2.5);
      }

      for (let i = 0; i < 61; ++i) {
        const idx = i + 195;
        colors[idx][0] = 255;
        colors[idx][1] = 255;
        colors[idx][2] = 230 + Math.min(25, i);
      }

      let time = Date.now();

      const renderFire = () => {
        const now = Date.now();
        const dt = now - time;
        if (dt < 1000 / fps) {
          animationFrameId = requestAnimationFrame(renderFire);
          return;
        }
        time = now;

        const bottom = canvasWidth * (canvasHeight - 1);

        for (let x = 0; x < canvasWidth; x++) {
          const stabilityFactor = 0.5 + (20 - normalizedLevel) / 20;
          const rand = Math.random() * stabilityFactor;
          fire[bottom + x] = rand > threshold ? 255 : 0;
        }

        for (let y = canvasHeight - 2; y >= 0; y--) {
          for (let x = 0; x < canvasWidth; x++) {
            const idx = y * canvasWidth + x;
            let sum = fire[idx + canvasWidth];

            if (x > 0) {
              sum += fire[idx + canvasWidth - 1] * 0.9;
            }
            if (x < canvasWidth - 1) {
              sum += fire[idx + canvasWidth + 1] * 0.9;
            }
            if (y < canvasHeight - 3 && x > 1) {
              sum += fire[idx + 2 * canvasWidth - 2] * 0.4;
            }
            if (y < canvasHeight - 3 && x < canvasWidth - 2) {
              sum += fire[idx + 2 * canvasWidth + 2] * 0.4;
            }

            sum /= 3.6;

            const heightEffect = fireHeightFactor * (1 - y / canvasHeight);
            let decay = speedFactor + ((canvasHeight - y) / canvasHeight) * 0.6;
            decay *= 1.3 - normalizedLevel / 30;
            decay *= 1 + heightEffect;

            fire[idx] = Math.max(0, sum - decay);
          }
        }

        for (let y = 2; y < canvasHeight; y++) {
          for (let x = 0; x < canvasWidth; x++) {
            const value = fire[(y - 2) * canvasWidth + x] | 0;
            const i = y * canvasWidth * 4 + x * 4;
            data[i] = colors[value][0];
            data[i + 1] = colors[value][1];
            data[i + 2] = colors[value][2];
            data[i + 3] = value === 0 ? 0 : 255;
          }
        }

        ctx.putImageData(imageData, 0, 0);
        animationFrameId = requestAnimationFrame(renderFire);
      };

      renderFire();
    }

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [dimensions, fireLevel, theme]);

  const canvasStyle = {
    imageRendering: theme === 'fire' ? 'pixelated' : 'auto',
    position: 'absolute',
    bottom: 0,
    left: 0,
	zIndex: -1,
    pointerEvents: 'none'
  };

  return (
    <canvas
      ref={canvasRef}
      width={dimensions.width}
      height={dimensions.height}
      style={canvasStyle}
    />
  );
}
